const MFA_ACR_VALUE = 'http://schemas.openid.net/pape/policies/2007/06/multi-factor';

const MFA_AMR_VALUES = new Set([
  'mfa',
  'otp',
  'phr',
  'passkey',
  'webauthn-roaming',
  'webauthn-platform',
]);

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) base64 += '='.repeat(4 - padding);
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalizeAmrValues(amr: unknown): string[] {
  if (Array.isArray(amr)) {
    return amr.filter((v): v is string => typeof v === 'string');
  }
  if (typeof amr === 'string') {
    return [amr];
  }
  return [];
}

function amrValuesIndicateMfa(values: string[]): boolean {
  return values.some((v) => MFA_AMR_VALUES.has(v.toLowerCase()));
}

export function extractAmrValuesFromClaims(
  claims: Record<string, unknown>,
  audienceHint?: string,
): string[] {
  const collected: string[] = [];

  const append = (amr: unknown) => {
    collected.push(...normalizeAmrValues(amr));
  };

  append(claims.amr);

  const audience = audienceHint?.trim().replace(/\/+$/, '');
  if (audience) {
    append(claims[`${audience}/amr`]);
  }

  for (const [key, value] of Object.entries(claims)) {
    if (key.endsWith('/amr')) {
      append(value);
    }
  }

  return collected;
}

/** True when JWT claims indicate MFA was completed (access token, ID token, or custom claim). */
export function isMfaSatisfiedFromTokenClaims(
  claims: Record<string, unknown> | undefined,
  audienceHint?: string,
): boolean {
  if (!claims) return false;

  const amrValues = extractAmrValuesFromClaims(claims, audienceHint);
  if (amrValuesIndicateMfa(amrValues)) return true;

  const acr = claims.acr;
  return typeof acr === 'string' && (acr === MFA_ACR_VALUE || acr.includes('multi-factor'));
}

/** True when access token claims indicate MFA was completed. */
export function isAccessTokenMfaSatisfied(accessToken: string | null | undefined): boolean {
  if (!accessToken?.trim()) return false;
  const payload = decodeJwtPayload(accessToken.trim());
  if (!payload) return false;
  return isMfaSatisfiedFromTokenClaims(
    payload,
    import.meta.env.VITE_AUTH0_AUDIENCE?.trim(),
  );
}

export const PORTAL_MFA_ACR_VALUES = MFA_ACR_VALUE;
